/**
 * Krok AS3 bod 1 — KONTROLY ASISTENTKY (`reos.run_checks`) nad TYMIZ
 * funkcemi, kterymi pocitaji obrazovky. Zadna kontrola si podminku nepise
 * znovu: kde ji mela jen obrazovka (kanban TAB1, editor smluv, kokpit
 * prověření), presunula se sem a obrazovka ji re-exportuje (SSOT, vzor AS2).
 *
 * Tenhle modul je CISTY: dostane data, ktera gateway nacte z tychz API cest
 * jako obrazovky, a vrati „zasahy" (`CheckHit`) s cisly a identifikatory.
 * Vety pro cloveka sklada volajici z katalogu (`@reos/i18n`, cs zdroj + en).
 *
 * Max 8 kontrol pri zalozeni (zadani AS3); dalsi pribyly samostatnymi kroky
 * (`report_stale`, `listing_no_reaction` z NAP2). Kazda stoji na sloupci,
 * ktery nekdo plni — kde
 * takovy sloupec neni, kontrola tvrdi jen to, co data unesou (viz
 * `dd_blocker_open`: „otevřený“, ne „neotevřený makléřem“).
 */
import { emailCardsOnYou, overdueInvoices, overdueTaskEntries, splitEntryTitle } from './index.js';
// ------------------------------------------------------------ sdilene predikaty
/** Dny ve fazi (zaokrouhleno dolu, minimalne 0) — kanban TAB1. */
export function daysInStage(stageEnteredAt, now) {
    const ms = now.getTime() - new Date(stageEnteredAt).getTime();
    return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
}
/** Krok TAB1 body 6/7/9 — „stojí DÉLE NEŽ 30 dní“ (ostre vetsi: cervene az 31. den). */
export const STAGE_STUCK_DAYS = 30;
export function isStuckInStage(stageEnteredAt, now) {
    return daysInStage(stageEnteredAt, now) > STAGE_STUCK_DAYS;
}
/**
 * Editor smluv (krok T4/SML2) — pocty chybejicich a odsunutych udaju smlouvy.
 *
 * Krok APP3 bod 1 (nalez A31) — CISLO JDE Z OSNOVY SERVERU.
 *
 * Do APP3 se scitaly `fieldOrigins`, tedy pole, ktera vzor VYKRESLUJE.
 * Povinne pole slovniku mimo text (krok EDIT2 bod 3) v nich neni, takze
 * kontrola Asistentky („Ve smlouvě chybí N povinných údajů") i radek stavu
 * v adminu rikaly jine cislo nez brana podpisu a nez panel kontrol — demo
 * smlouva Sojovice ukazovala „zbývá 9" a „10 chybí" soucasne.
 *
 * `fillPlan` (osnova ze serveru) je JEDINY zdroj tech cisel. `fieldOrigins`
 * zustava zachrannou siti pro volajici, kteri osnovu nemaji (radek smlouvy
 * z doby pred APP3, cache detailu) — jinak by cislo spadlo na nulu a kontrola
 * by mlcela.
 */
export function contractCounts(detail) {
    const plan = detail.fillPlan;
    if (plan !== undefined && typeof plan.missing === 'number' && typeof plan.later === 'number') {
        return { errors: plan.missing, later: plan.later };
    }
    let errors = 0;
    let later = 0;
    for (const origin of Object.values(detail.fieldOrigins ?? {})) {
        if (origin === 'chybi')
            errors += 1;
        else if (origin === 'pozdeji')
            later += 1;
    }
    return { errors, later };
}
/** Kokpit prověření (UXR2) — otazka, ktera brani prodeji: zavreny pristup nebo kriticky dopad. */
export const ACCESS_OBSTACLE_QUESTION_PREFIX = 'Q-ACCESS-OBSTACLE-';
export function isBlockerQuestion(question) {
    return question.questionKey.startsWith(ACCESS_OBSTACLE_QUESTION_PREFIX) || question.impact === 'CRITICAL';
}
/** Otazka je jeste ve hre (neni zodpovezena). */
export function isOpenQuestion(question) {
    return question.status !== 'ANSWERED';
}
/** Faze, ve kterych je nemovitost inzerovana (kanban: Inzerce → Jednání). */
export const ACTIVE_LISTING_STAGES = ['listing', 'viewings', 'negotiation'];
/** Report starsi nez tohle u aktivni inzerce = data v inzeratu mohou lhat. */
export const REPORT_STALE_DAYS = 90;
/**
 * Krok NAP2 bod A13 — po kolika dnech BEZ REAKCE ZAJEMCE se inzerat
 * pripomene. Hodnota je nastaveni kancelare
 * (`crm_notification_settings.listing_no_reaction_days`, migrace crm 0068);
 * tahle konstanta plati, kdyz se nastaveni nenacte. Hranice je OSTRA (`>`),
 * tedy pri 14 se inzerat ozve 15. den — stejne jako `STAGE_STUCK_DAYS`.
 */
export const LISTING_NO_REACTION_DEFAULT_DAYS = 14;
/** Poptavka z e-mailu (AS1), na kterou nikdo nereagoval dele nez 24 h. */
export const EMAIL_INQUIRY_STALE_HOURS = 24;
export function daysSince(iso, now) {
    return Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000));
}
/** Zitrek v MISTNIM case volajiciho (prohlidka „zitra“ = kalendarni den). */
export function isTomorrow(iso, now) {
    const at = new Date(iso);
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    return at.getFullYear() === tomorrow.getFullYear() && at.getMonth() === tomorrow.getMonth() && at.getDate() === tomorrow.getDate();
}
/** Prohlidka zitra, kterou nikdo nepotvrdil (`crm_calendar_events.status = tentative`). */
export function isUnconfirmedViewingTomorrow(event, now) {
    return event.eventType === 'viewing' && event.status === 'tentative' && isTomorrow(event.startsAt, now);
}
/**
 * Krok NAP2 bod A13 — kolik dni inzerat ZAKAZKY nedostal reakci zajemce.
 *
 * Odkud se bere: posledni poptavka ze PORTALU u teto zakazky
 * (`crm_demands.source='portal'`, tentyz zdroj jako reakce v reportu pro
 * majitele). Kdyz zadna nebyla, meri se od vstupu do faze inzerce
 * (`cases.stage_entered_at`) — inzerat, ktery nikdy nic nedostal, je presne
 * ten pripad, na ktery se ma makler podivat.
 *
 * `null` = nejde rict (zakazka neni v inzerci, nebo nezname datum).
 */
export function listingDaysWithoutReaction(input, now) {
    if (!ACTIVE_LISTING_STAGES.includes(input.stage))
        return null;
    const since = input.lastReactionAt ?? input.stageEnteredAt;
    if (since === '' || Number.isNaN(new Date(since).getTime()))
        return null;
    return daysSince(since, now);
}
/** Karta „Nové z e-mailu“ druhu poptávka, otevrena dele nez 24 h (AS1 + `emailCardsOnYou`). */
export function staleEmailInquiries(cards, now) {
    const limit = EMAIL_INQUIRY_STALE_HOURS * 3_600_000;
    return emailCardsOnYou(cards).filter((card) => card.kind === 'poptavka' && now.getTime() - new Date(card.receivedAt).getTime() > limit);
}
// -------------------------------------------------------------------- vystup
export const CHECK_CODES = [
    'case_stuck',
    'contract_awaiting_signature',
    'contract_missing_fields',
    'viewing_unconfirmed',
    'dd_blocker_open',
    'email_inquiry_stale',
    'invoice_overdue',
    'task_overdue',
    'report_stale',
    'listing_no_reaction',
];
/**
 * Pravidla zavaznosti (pravidla napric projekty §checks): `high` jen na to,
 * co ma termin dnes/zitra (prohlidka zitra, poptavka chladne). Chronicke
 * stavy (stojici zakazka, stary report) jsou `low`. Faktura po splatnosti je
 * JEN upozorneni (zadani) — `low` a vlastnik kancelar: Asistentka penize neresi.
 */
export function collectCheckHits(input, now) {
    const hits = [];
    const caseById = new Map(input.cases.map((c) => [c.id, c]));
    const caseRef = (id) => `ZAK ${id.slice(0, 8)}`;
    const caseTitle = (id) => (id === null ? '' : (caseById.get(id)?.title ?? ''));
    for (const c of input.cases) {
        if (c.stage === 'closed' || c.stage === 'cancelled')
            continue;
        if (isStuckInStage(c.stageEnteredAt, now)) {
            hits.push({
                code: 'case_stuck', key: c.id, severity: 'low', urgency: 'later', bucket: 'zakazky', owner: 'makler', waitingFor: null,
                ref: caseRef(c.id), subject: c.title, path: `/zakazky/${c.id}`,
                values: { days: daysInStage(c.stageEnteredAt, now), stage: c.stage },
            });
        }
    }
    for (const contract of input.contractsAwaiting) {
        hits.push({
            code: 'contract_awaiting_signature', key: contract.id, severity: 'medium', urgency: 'week', bucket: 'smlouvy', owner: 'makler', waitingFor: 'klient',
            ref: `SML ${contract.id.slice(0, 8)}`, subject: contract.title, path: `/smlouvy/${contract.id}`,
            values: { caseTitle: caseTitle(contract.caseId) },
        });
    }
    for (const draft of input.contractDrafts) {
        const { errors } = contractCounts(draft);
        if (errors > 0) {
            hits.push({
                code: 'contract_missing_fields', key: draft.id, severity: 'medium', urgency: 'week', bucket: 'smlouvy', owner: 'makler', waitingFor: null,
                ref: `SML ${draft.id.slice(0, 8)}`, subject: draft.title, path: `/smlouvy/${draft.id}`,
                values: { count: errors },
            });
        }
    }
    for (const event of input.calendar) {
        if (isUnconfirmedViewingTomorrow(event, now)) {
            hits.push({
                code: 'viewing_unconfirmed', key: event.id, severity: 'high', urgency: 'today', bucket: 'kalendar', owner: 'makler',
                // Potvrdit musi MAKLER (zavolat zajemci) — je to ukol, ne „čekáme na“.
                waitingFor: null,
                // Nazev „Prohlídka — Topolová 912“ se deli STEJNE jako na Dnesku (`splitEntryTitle`).
                ref: `UDÁLOST ${event.id.slice(0, 8)}`, subject: splitEntryTitle(event.title, null).detail ?? event.title, path: '/kalendar',
                values: { startsAt: event.startsAt },
            });
        }
    }
    const blockersByCase = new Map();
    for (const q of input.questions) {
        if (isOpenQuestion(q) && isBlockerQuestion(q))
            blockersByCase.set(q.caseId, (blockersByCase.get(q.caseId) ?? 0) + 1);
    }
    for (const [caseId, count] of blockersByCase) {
        hits.push({
            code: 'dd_blocker_open', key: caseId, severity: 'medium', urgency: 'week', bucket: 'provereni', owner: 'makler', waitingFor: null,
            ref: caseRef(caseId), subject: caseTitle(caseId), path: `/zakazky/${caseId}`,
            values: { count },
        });
    }
    for (const card of staleEmailInquiries(input.emailCards, now)) {
        hits.push({
            code: 'email_inquiry_stale', key: card.id, severity: 'high', urgency: 'today', bucket: 'posta', owner: 'makler', waitingFor: null,
            ref: `E-MAIL ${card.id.slice(0, 8)}`, subject: card.subject ?? '', path: '/ukoly',
            values: { hours: Math.floor((now.getTime() - new Date(card.receivedAt).getTime()) / 3_600_000), from: card.fromName ?? card.fromEmail ?? '' },
        });
    }
    for (const invoice of overdueInvoices(input.invoices, now)) {
        hits.push({
            code: 'invoice_overdue', key: invoice.id, severity: 'low', urgency: 'week', bucket: 'finance', owner: 'kancelar', waitingFor: null,
            ref: `FA ${invoice.number ?? invoice.id.slice(0, 8)}`, subject: invoice.customerName, path: '/fakturace',
            values: { days: daysSince(invoice.dueDate, now), total: invoice.total ?? '' },
        });
    }
    for (const entry of overdueTaskEntries(input.tasks)) {
        const id = entry.id.replace(/^task-/, '');
        hits.push({
            code: 'task_overdue', key: id, severity: 'medium', urgency: 'today', bucket: 'ukoly', owner: 'makler', waitingFor: null,
            ref: `ÚKOL ${id.slice(0, 8)}`, subject: entry.title, path: '/ukoly',
            values: { dueAt: entry.at ?? '' },
        });
    }
    /*
     * Krok NAP2 bod A13 — „inzerát bez reakce X dní". X je nastaveni kancelare
     * (`crm_notification_settings.listing_no_reaction_days`), vychozi 14.
     * Posledni reakce = nejnovejsi poptavka z portalu u zakazky.
     */
    const limitDays = input.listingNoReactionDays ?? LISTING_NO_REACTION_DEFAULT_DAYS;
    const lastReactionByCase = new Map();
    for (const reaction of input.portalReactions ?? []) {
        const current = lastReactionByCase.get(reaction.caseId);
        if (current === undefined || reaction.createdAt > current) {
            lastReactionByCase.set(reaction.caseId, reaction.createdAt);
        }
    }
    for (const c of input.cases) {
        const lastReactionAt = lastReactionByCase.get(c.id) ?? null;
        const days = listingDaysWithoutReaction({ stage: c.stage, stageEnteredAt: c.stageEnteredAt, lastReactionAt }, now);
        if (days === null || days <= limitDays)
            continue;
        hits.push({
            code: 'listing_no_reaction', key: c.id, severity: 'low', urgency: 'week', bucket: 'zakazky', owner: 'makler', waitingFor: null,
            ref: caseRef(c.id), subject: c.title, path: `/zakazky/${c.id}`,
            // `everReacted` rozhoduje, kterou vetu katalog slozi: „zadna reakce od
            // zacatku inzerce" vs. „posledni reakce pred X dny".
            values: { days, limit: limitDays, everReacted: lastReactionAt === null ? 'no' : 'yes' },
        });
    }
    const lastRun = new Map(input.auditRuns.map((r) => [r.dealId, r.lastFinishedAt]));
    for (const c of input.cases) {
        if (!ACTIVE_LISTING_STAGES.includes(c.stage))
            continue;
        const finished = lastRun.get(c.id) ?? null;
        // Zakazka bez dobehleho behu v inzerci nema report vubec — to je horsi
        // nez stary, proto se hlasi taky (s `days` = -1 → veta „report chybí“).
        if (finished === null || daysSince(finished, now) > REPORT_STALE_DAYS) {
            hits.push({
                code: 'report_stale', key: c.id, severity: 'low', urgency: 'week', bucket: 'zakazky', owner: 'makler', waitingFor: null,
                ref: caseRef(c.id), subject: c.title, path: `/zakazky/${c.id}`,
                values: { days: finished === null ? -1 : daysSince(finished, now) },
            });
        }
    }
    return hits;
}
/**
 * „1 zakázka stojí / 2 zakázky stojí / 5 zakázek stojí“ — skloneni poctu pro
 * vety, ktere se NEsklada v katalogu (pravidla napric projekty §1, `skl`).
 */
export function skl(n, one, twoToFour, fivePlus) {
    const abs = Math.abs(n);
    if (abs === 1)
        return one;
    if (abs >= 2 && abs <= 4)
        return twoToFour;
    return fivePlus;
}
//# sourceMappingURL=checks.js.map